<template>
  <div class="app-navigation">
    <InspectorControl v-if="!showTopNavigation" floating />
    <v-navigation-drawer
      v-if="showLeftNavigation"
      v-model="drawer"
      app
      :temporary="isMobile"
      :permanent="!isMobile"
      :expand-on-hover="!isMobile && !guideMenuOpen"
      :mini-variant="!isMobile && !guideMenuOpen"
      :width="280"
      :mini-variant-width="64"
      class="app-navigation__drawer"
    >
      <v-list class="app-navigation__identity py-2">
        <v-list-item class="px-3" @click="goToMenuHead">
          <v-progress-circular
            :indeterminate="axiosLoading"
            :color="user.color || 'indigo'"
            value="100"
            :size="42"
            :width="2"
          >
            <v-avatar size="34" :color="user.color || 'indigo'">
              <v-img v-if="user.picture" :src="user.picture" />
              <span v-else class="white--text text-caption font-weight-bold">{{ userInitials }}</span>
            </v-avatar>
          </v-progress-circular>
          <v-list-item-content class="ml-3 py-0">
            <v-list-item-title class="font-weight-medium">{{ userFullName }}</v-list-item-title>
            <v-list-item-subtitle v-if="lastConnectionLabel" class="app-navigation__last-connection">Dernière connexion : {{ lastConnectionLabel }}</v-list-item-subtitle>
            <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider />

      <v-list nav dense class="px-2">
        <draggable
          v-if="pagesLeft.length"
          class="menu"
          :list="pagesLeft"
          group="b"
          handle=".handle"
          :disabled="!isAdmin"
          @change="savePageOrder(pagesLeft)"
        >
          <div v-for="route in pagesLeft" :key="route.id || route.url">
            <v-list-item
              v-if="!hasChildren(route)"
              :data-guide-target="`menu:page:${route.id}`"
              link
              :class="{ 'app-navigation__active-item': isCurrentRoute(route) }"
              @click="goToPage(route)"
            >
              <v-list-item-icon>
                <v-icon>{{ routeIcon(route) || 'mdi-circle-small' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ routeName(route) }}<rights-indicator :checks="[{core:'page',id:route.id}]" :label="routeName(route)" /></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="isAdmin" class="my-0">
                <v-icon small class="handle">mdi-drag-vertical</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-group v-else :value="guideMenuOpen" :prepend-icon="routeIcon(route) || 'mdi-folder-outline'" no-action>
              <template #activator>
                <v-list-item-content>
                  <v-list-item-title>{{ parentRouteName(route) }}<rights-indicator :checks="[{core:'page',id:route.id}]" :label="parentRouteName(route)" /></v-list-item-title>
                </v-list-item-content>
                <v-icon v-if="isAdmin" small class="handle mr-1">mdi-drag-vertical</v-icon>
              </template>

              <v-list-item link :data-guide-target="`menu:page:${route.id}`" class="app-navigation__subitem" @click="goToPage(route)">
                <v-list-item-icon><v-icon small>mdi-arrow-right</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>{{ routeName(route) }}<rights-indicator :checks="[{core:'page',id:route.id}]" :label="routeName(route)" /></v-list-item-title></v-list-item-content>
              </v-list-item>

              <draggable
                class="submenu"
                :list="route.children"
                group="c"
                handle=".subhandle"
                :disabled="!isAdmin"
                @change="savePageOrder(route.children)"
              >
                <v-list-item
                  v-for="subpage in route.children"
                  :key="subpage.id || subpage.url"
                  :data-guide-target="`menu:page:${subpage.id}`"
                  link
                  class="app-navigation__subitem"
                  @click="goToPage(subpage)"
                >
                  <v-list-item-icon><v-icon small>{{ routeIcon(subpage) || 'mdi-arrow-right' }}</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title>{{ routeName(subpage) }}<rights-indicator :checks="[{core:'page',id:subpage.id}]" :label="routeName(subpage)" /></v-list-item-title></v-list-item-content>
                  <v-list-item-action v-if="isAdmin" class="my-0"><v-icon small class="subhandle">mdi-drag-vertical</v-icon></v-list-item-action>
                </v-list-item>
              </draggable>
            </v-list-group>
          </div>
        </draggable>
      </v-list>
    </v-navigation-drawer>

    <v-navigation-drawer
      v-if="rightNavigationEnabled && canUsePrivateNavigation && isAdmin"
      v-model="drawerRight"
      right
      app
      :temporary="$vuetify.breakpoint.smAndDown"
      width="360"
      class="app-navigation__right-drawer"
    >
      <main-component v-if="idPageRightSide" :page="idPageRightSide" hook="rightside" />
    </v-navigation-drawer>

    <v-app-bar
      v-if="showTopNavigation"
      app
      dark
      flat
      height="54"
      class="app-navigation__bar"
    >
      <v-app-bar-nav-icon v-if="isMobile" aria-label="Ouvrir la navigation" @click="drawer = !drawer">
        <v-badge :value="axiosLoading" dot color="orange" overlap>
          <v-avatar size="30" :color="user.color || 'indigo lighten-1'">
            <v-img v-if="user.picture" :src="user.picture" />
            <span v-else class="text-caption font-weight-bold">{{ userInitials }}</span>
          </v-avatar>
        </v-badge>
      </v-app-bar-nav-icon>

      <v-btn
        v-if="canUsePrivateNavigation && currentPage"
        icon
        small
        :aria-label="isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'"
        @click="putFavorite('topfavoris')"
      >
        <v-icon :color="isFav ? 'amber accent-2' : 'white'">{{ isFav ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
      </v-btn>

      <div class="app-navigation__title ml-2">
        <div class="app-navigation__page-title text-truncate">{{ currentPageTitle }}</div>
        <div v-if="user.companie" class="app-navigation__company text-truncate">{{ user.companie }}</div>
      </div>

      <div v-if="canUsePrivateNavigation && !$vuetify.breakpoint.smAndDown" class="app-navigation__shortcuts ml-3">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="goToAccueil"><v-icon>mdi-home-outline</v-icon></v-btn>
          </template>
          <span>Page d'accueil</span>
        </v-tooltip>
        <v-tooltip v-for="favorite in topFavorites" :key="favoriteKey(favorite)" bottom>
          <template #activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" @click="goToFav(favorite)">
              <v-icon v-if="favorite.icon">{{ favorite.icon }}</v-icon>
              <span v-else class="text-caption font-weight-bold">{{ firstLetter(favorite.displayname) }}</span>
            </v-btn>
          </template>
          <span>Raccourci vers {{ favorite.displayname }}</span>
        </v-tooltip>
      </div>

      <v-spacer />

      <InspectorControl class="mr-2" />

      <v-text-field
        v-if="canUsePrivateNavigation && idPageSearch && !$vuetify.breakpoint.smAndDown"
        v-model="resultSearch"
        class="app-navigation__search mr-2"
        placeholder="Rechercher…"
        prepend-inner-icon="mdi-magnify"
        rounded
        dense
        filled
        hide-details
        clearable
        @input="updateSearch"
        @keydown.enter.prevent="submitSearch"
      />




      <v-tooltip v-if="canUsePrivateNavigation" bottom><template #activator="{on,attrs}"><v-btn icon v-bind="attrs" v-on="on" aria-label="Aide et visites guidées" data-guide-target="help:open" @click="openGuides"><v-icon>mdi-help-circle-outline</v-icon></v-btn></template><span>Aide et visites guidées</span></v-tooltip>

      <v-menu v-if="canUsePrivateNavigation" offset-y left max-width="420">
        <template #activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" aria-label="Notifications">
            <v-badge :value="notifications.length" :content="notifications.length" color="red" overlap>
              <v-icon :class="{ shake: isShaking }" :color="notifications.length ? 'amber accent-2' : 'white'">mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <v-card min-width="320">
          <v-card-title class="text-subtitle-1 py-3">Notifications</v-card-title>
          <v-divider />
          <v-list v-if="notifications.length" dense class="py-0">
            <v-list-item v-for="notification in notifications" :key="notification.key || notification.time">
              <v-list-item-icon><v-icon color="indigo">mdi-information-outline</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ notification.message }}</v-list-item-title>
                <v-list-item-subtitle>{{ moment(notification.time).format('HH:mm') }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action><v-btn icon small @click="$store.dispatch('removeNotifications', notification)"><v-icon small>mdi-close</v-icon></v-btn></v-list-item-action>
            </v-list-item>
          </v-list>
          <v-card-text v-else class="grey--text text-center">Aucune notification</v-card-text>
        </v-card>
      </v-menu>

      <v-tooltip v-if="canUsePrivateNavigation && !$vuetify.breakpoint.smAndDown" bottom>
        <template #activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" @click="putFavorite('onopen')">
            <v-icon :color="isDemmarage ? 'pink lighten-3' : 'white'">{{ isDemmarage ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ isDemmarage ? 'Page de démarrage active' : 'Définir comme page de démarrage' }}</span>
      </v-tooltip>

      <v-tooltip v-if="!isMobile || !showMoreMenu" bottom>
        <template #activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" :loading="savingThemeMode" @click="toggleThemeMode">
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ isDarkMode ? 'Passer au thème clair' : 'Passer au thème sombre' }}</span>
      </v-tooltip>

      <v-tooltip v-if="!isMobile || !showMoreMenu" bottom>
        <template #activator="{ on, attrs }"><v-btn icon v-bind="attrs" v-on="on" @click="logout"><v-icon>mdi-logout</v-icon></v-btn></template>
        <span>Se déconnecter</span>
      </v-tooltip>

      <v-menu v-if="showMoreMenu" left bottom offset-y :close-on-content-click="false">
        <template #activator="{ on, attrs }"><v-btn icon v-bind="attrs" v-on="on" aria-label="Options du compte"><v-icon>mdi-dots-vertical</v-icon></v-btn></template>
        <v-list nav dense min-width="280">
          <div v-if="profiles.length" class="pa-3">
            <v-select
              :items="profiles"
              item-text="text"
              item-value="value"
              return-object
              label="Changer de profil"
              prepend-inner-icon="mdi-account-switch-outline"
              color="primary"
              outlined
              dense
              hide-details
              :menu-props="{ offsetY: true, maxHeight: 320 }"
              @change="changeProfil"
            />
          </div>
          <v-list-item v-if="isAdmin" :to="{ name: 'engine-dashboard' }">
            <v-list-item-icon><v-icon>mdi-view-dashboard-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Pilotage du système</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAdmin" @click="createPage">
            <v-list-item-icon><v-icon>mdi-plus</v-icon></v-list-item-icon>
            <v-list-item-title>Nouvelle page</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="$vuetify.breakpoint.smAndDown && idPageSearch" @click="openMobileSearch">
            <v-list-item-icon><v-icon>mdi-magnify</v-icon></v-list-item-icon>
            <v-list-item-title>Rechercher</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="$vuetify.breakpoint.smAndDown" @click="putFavorite('onopen')">
            <v-list-item-icon><v-icon>{{ isDemmarage ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon></v-list-item-icon>
            <v-list-item-title>Page de démarrage</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isMobile" :disabled="savingThemeMode" @click="toggleThemeMode">
            <v-list-item-icon><v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon></v-list-item-icon>
            <v-list-item-title>{{ isDarkMode ? 'Passer au thème clair' : 'Passer au thème sombre' }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isMobile" @click="logout">
            <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
            <v-list-item-title>Se déconnecter</v-list-item-title>
          </v-list-item>
          <v-divider v-if="pagesTop.length" />
          <v-list-item v-for="route in pagesTop" :data-guide-target="`menu:page:${route.id}`" :key="route.id || route.url" @click="goToPage(route)">
            <v-list-item-icon><v-icon>{{ routeIcon(route) || 'mdi-link' }}</v-icon></v-list-item-icon>
            <v-list-item-title>{{ routeName(route) }}<rights-indicator :checks="[{core:'page',id:route.id}]" :label="routeName(route)" /></v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-dialog v-model="menusearch" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card class="app-navigation__search-dialog">
        <v-toolbar flat dark class="app-navigation__search-toolbar">
          <v-btn icon @click="menusearch = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-text-field
            v-model="resultSearch"
            autofocus
            solo-inverted
            flat
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Rechercher…"
            class="mx-3"
            @input="updateSearch"
            @keydown.enter.prevent="submitSearch"
          />
        </v-toolbar>
        <v-card-text class="pa-0 app-navigation__search-results">
          <main-component v-if="menusearch && reloadSearch && idPageSearch" :page="idPageSearch" type="page" hook="search" :key="reloadSearch" />
          <div v-else class="pa-8 text-center grey--text">
            <v-icon large color="grey lighten-1">mdi-text-search</v-icon>
            <div class="mt-2">Saisissez un terme puis appuyez sur Entrée.</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import InspectorControl from '@/components/engine/rightsInspection/InspectorControl.vue'
import { registerGuideTarget, visibleGuideElement } from '@/components/engine/guide/registry'
import { openGuideLibrary, guideState } from '@/components/engine/guide/state'

export default {
  name: 'NavBar',
  components: { draggable, InspectorControl },
  data() {
    return {
      drawer: false,
      guideMenuOpen: false,
      guideDrawerWasOpen: null,
      guideSession: guideState,
      // Panneau conservé pour un usage futur, sans montage ni espace réservé.
      rightNavigationEnabled: false,
      drawerRight: false,
      menusearch: false,
      resultSearch: '',
      idPageSearch: false,
      idPageRightSide: false,
      idPageMenuHead: false,
      reloadSearch: 0,
      pagesLeft: [],
      pagesTop: [],
      pageAll: [],
      listFavories: { topfavoris: [], onopen: [] },
      profiles: [],
      isFav: false,
      isDemmarage: false,
      isShaking: false,
      shakeTimer: null,
      savingThemeMode: false
    }
  },
  computed: {
    user() { return this.$store.getters.currentUser || {} },
    currentPage() { return this.$store.getters.currentPage },
    axiosLoading() { return this.$store.getters.axiosLoading },
    notifications() { return this.$store.getters.notifications || [] },
    isMobile() { return this.$vuetify.breakpoint.smAndDown },
    isAdmin() { return Number(this.$env.getTypeUser()) === 3 },
    isDarkMode() { return Boolean(this.user.preferences && this.user.preferences.darkmode) },
    canUsePrivateNavigation() { return this.$store.getters.isPublic() },
    showLeftNavigation() { return this.$store.getters.isPublic('displayleft') },
    showTopNavigation() { return this.$store.getters.isPublic('displaytop') },
    topFavorites() { return Array.isArray(this.listFavories.topfavoris) ? this.listFavories.topfavoris : [] },
    startFavorite() { return this.listFavories.onopen || [] },
    userFullName() { return [this.user.name, this.user.nickname].filter(Boolean).join(' ') },
    lastConnectionLabel() {
      if (!this.user.last_connect) return ''
      const date = this.moment(this.user.last_connect)
      return date.isValid() ? date.format('DD/MM/YYYY à HH:mm') : ''
    },
    userInitials() { return `${this.firstLetter(this.user.name)}${this.firstLetter(this.user.nickname)}` || '?' },
    currentPageTitle() {
      if (this.$route.name === 'engine-dashboard') return 'Pilotage du système'
      if(!this.currentPage) return 'Accueil'
      const page = this.$store.getters.specificPage(this.currentPage)
      if(!page || !page.displayname) return 'Accueil'
      return this.replaceBy({ text: page.displayname, page: this.currentPage })
    },
    showMoreMenu() {
      return this.canUsePrivateNavigation && (this.isAdmin || this.isMobile || this.pagesTop.length > 0 || this.profiles.length > 0)
    }
  },
  watch: {
    'guideSession.running'(running) { if (!running) this.closeGuideMenu() },
    '$store.getters.notifications.length'(newValue, oldValue) {
      if(!newValue || newValue <= oldValue) return
      this.isShaking = true
      clearTimeout(this.shakeTimer)
      this.shakeTimer = setTimeout(() => { this.isShaking = false }, 3000)
    },
    $route: {
      handler() { this.syncFavoriteState() },
      deep: true
    },
    listFavories: {
      handler() { this.syncFavoriteState() },
      deep: true
    }
  },
  created() {
    this.loadNavigation()
    this.loadFavorites()
  },
  mounted() {
    this.guideCleanup = [registerGuideTarget('menu:open', { label: 'Ouvrir la navigation', element: () => this.$el.querySelector('.app-navigation__bar'), operations: { open: () => this.openGuideMenu() } })]
    this.profiles = JSON.parse(JSON.stringify(this.$store.getters.listProfils || []))
  },
  beforeDestroy() {
    for (const cleanup of this.guideCleanup || []) cleanup()
    clearTimeout(this.shakeTimer)
  },
  methods: {
    openGuides() { openGuideLibrary() },
    openGuideMenu() {
      if (!this.guideMenuOpen) this.guideDrawerWasOpen = this.drawer
      this.drawer = true
      this.guideMenuOpen = true
      return this.$nextTick()
    },
    closeGuideMenu() {
      if (!this.guideMenuOpen) return
      this.guideMenuOpen = false
      this.drawer = this.guideDrawerWasOpen
      this.guideDrawerWasOpen = null
    },
    registerGuidePages() {
      const pages = this.pageAll.flatMap(page => [page, ...(page.children || [])])
      for (const page of pages) {
        const key = `menu:page:${page.id}`
        this.guideCleanup.push(registerGuideTarget(key, { label: this.routeName(page), element: () => Array.from(this.$el.querySelectorAll('[data-guide-target]')).find(element => element.getAttribute('data-guide-target')===key && visibleGuideElement(element)), operations: { click: () => this.goToPage(page), open: () => this.openGuideMenu() } }))
      }
    },
    firstLetter(value) { return value ? String(value).trim().charAt(0).toUpperCase() : '' },
    routeParams(value) { return value && !Array.isArray(value) ? value : {} },
    routeIcon(route) { return route && route.displays && route.displays.params ? route.displays.params.icon : '' },
    routeName(route) { return route && route.displays ? route.displays.displayname : '' },
    parentRouteName(route) { return route.displays.params.displayifparent || this.routeName(route) },
    hasChildren(route) { return Array.isArray(route.children) && route.children.length > 0 },
    isCurrentRoute(route) { return this.$route.path === route.url },
    favoriteKey(favorite) { return `${favorite.name}-${JSON.stringify(favorite.params || {})}-${JSON.stringify(favorite.querys || {})}` },
    sameRoute(favorite, route = this.$route) {
      if(!favorite || favorite.name !== route.name) return false
      return JSON.stringify(this.routeParams(favorite.params)) === JSON.stringify(this.routeParams(route.params))
        && JSON.stringify(this.routeParams(favorite.querys)) === JSON.stringify(this.routeParams(route.query))
    },
    syncFavoriteState() {
      this.isFav = this.topFavorites.some(favorite => this.sameRoute(favorite))
      this.isDemmarage = !Array.isArray(this.startFavorite) && this.sameRoute(this.startFavorite)
    },
    async loadNavigation() {
      const response = await this.API({ type: 'get', url: 'allpage' })
      this.pagesLeft = []
      this.pagesTop = []
      this.pageAll = []
      response.data.forEach(page => {
        const position = page.displays && page.displays.params ? page.displays.params.position : ''
        if(position === 'left') this.pagesLeft.push(page)
        else if(position === 'top') this.pagesTop.push(page)
        else if(position === 'search') this.idPageSearch = page.id
        else if(position === 'menuhead') this.idPageMenuHead = page.name
        else if(position === 'rightside') this.idPageRightSide = page.id
        this.pageAll.push(page)
      })
      await this.$nextTick()
      this.registerGuidePages()
    },
    async loadFavorites() {
      const response = await this.API({ type: 'get', url: 'favorite' })
      this.listFavories = { topfavoris: [], onopen: [], ...(response.data || {}) }
    },
    goToMenuHead() { if(this.idPageMenuHead) this.$router.push({ name: this.idPageMenuHead }).catch(() => {}) },
    async goToPage(route) {
      const result = await this.$router.push({ path: route.url }).catch(error => { if (error.name !== 'NavigationDuplicated') throw error })
      this.closeGuideMenu()
      return result
    },
    goToAccueil() { this.$router.push('/').catch(() => {}) },
    goToFav(favorite) {
      this.$router.replace({
        name: favorite.name,
        params: this.routeParams(favorite.params),
        query: this.routeParams(favorite.querys)
      }).catch(() => {})
    },
    updateSearch(value) {
      this.$store.dispatch('changeStringSearch', value || '')
    },
    submitSearch() {
      if(!String(this.resultSearch || '').trim()) return
      this.updateSearch(this.resultSearch)
      this.reloadSearch += 1
      this.menusearch = true
    },
    openMobileSearch() {
      this.menusearch = true
    },
    logout() { this.$store.dispatch('logOut') },
    async toggleThemeMode() {
      const darkmode = !this.isDarkMode
      this.savingThemeMode = true
      try {
        const response = await this.API({ type: 'put', url: 'engine/personnel/theme-mode', params: { darkmode } })
        this.$store.commit('updateUser', { keys: ['preferences'], value: response.data.preferences })
      } finally {
        this.savingThemeMode = false
      }
    },
    savePageOrder(pages) {
      this.API({ type: 'post', url: 'pageorder', params: pages.map(page => page.id) })
    },
    async putFavorite(type) {
      const route = this.$route
      const query = { ...route.query }
      delete query.date
      const matched = route.matched.find(item => item.props && item.props.default && item.props.default.page)
      const pageId = matched ? matched.props.default.page : this.currentPage
      const currentDefinition = this.pageAll.find(page => page.name === route.name)

      if(currentDefinition && currentDefinition.url === '/' && !Object.keys(query).length && !Object.keys(route.params || {}).length) return

      const response = await this.API({
        type: 'post',
        url: 'favorite',
        params: { type, id: pageId, name: route.name, params: route.params, querys: query }
      })
      this.listFavories = { topfavoris: [], onopen: [], ...(response.data || {}) }
    },
    createPage() {
      this.createNewPage({
        callback: data => {
          this.$router.push({ name: data[0].name })
          this.drawerRight = false
          setTimeout(() => {
            this.ModifTemplate = true
            this.$store.dispatch('setActivTab', { hook: 'listElemTab', tab: 'parampage' })
          }, 500)
        }
      })
    },
    changeProfil(model) {
      this.API({ type: 'post', url: 'changeProfil', params: model }).then(response => {
        if(!response.data.success) {
          this.$toast.warning(response.data.message)
          return
        }
        window.location.href = '/'
      })
    }
  }
}
</script>

<style scoped>
.app-navigation__bar {
  background: linear-gradient(110deg, var(--app-navigation, #1a237e) 0%, var(--app-navigation-accent, #1565c0) 100%) !important;
  border-bottom: 1px solid rgba(255, 255, 255, .14) !important;
}
.app-navigation__drawer { border-right: 1px solid #e8eaf0; background: var(--app-navigation-drawer, #fff) !important; }
.app-navigation__identity { background: var(--app-navigation-drawer, #fff); }
.app-navigation__last-connection { font-size: .65rem; line-height: 1.5; }
.app-navigation__active-item { background: var(--app-navigation-drawer-active, #e8eaf6); color: #283593; }
.app-navigation__subitem { margin-left: 4px; border-radius: 6px; }
.app-navigation__title { min-width: 110px; max-width: 300px; line-height: 1.08; }
.app-navigation__page-title { font-size: .92rem; font-weight: 600; }
.app-navigation__company { margin-top: 2px; color: rgba(255,255,255,.72); font-size: .68rem; }
.app-navigation__shortcuts { display: flex; align-items: center; gap: 2px; padding-left: 10px; border-left: 1px solid rgba(255,255,255,.18); }
.app-navigation__search { flex: 0 1 280px; max-width: 280px; }
.app-navigation__search ::v-deep .v-input__slot { min-height: 34px !important; background: rgba(255,255,255,.14) !important; }
.app-navigation__search-toolbar {
  background: linear-gradient(110deg, var(--app-navigation, #1a237e), var(--app-navigation-accent, #1565c0)) !important;
}
.app-navigation__search-results { min-height: calc(100vh - 54px); max-height: calc(100vh - 54px); overflow-y: auto; background: var(--app-page, #f5f6fa); }
.handle, .subhandle { cursor: grab; }
.shake { display: inline-block; animation: navbar-shake .5s infinite; }
@keyframes navbar-shake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-14deg); }
  50% { transform: rotate(14deg); }
  75% { transform: rotate(-8deg); }
}
@media (max-width: 960px) {
  .app-navigation__title { flex: 1 1 auto; min-width: 0; max-width: none; }
}
@media (max-width: 600px) {
  .app-navigation__title { margin-left: 4px !important; }
  .app-navigation__page-title { font-size: .88rem; }
}
</style>
